import React, { useEffect, useRef, useState } from "react";

import { SpinnerIcon, RefreshIcon } from "@/components/icons";
import { Button } from "@/components/ui/Button";
import { Modal } from "@/components/ui/Modal";
import { formatDateTime } from "@/utils/date";

import { adsService } from "../services/adsService";
import type {
  BoostRecommendation,
  BoostTargeting,
  StandaloneAdRequest,
} from "../types";

type Phase = "recommending" | "ready" | "creating";

interface PostShape {
  id: string;
  caption?: string | null;
  postType?: string;
  isReel?: boolean;
}

interface StandaloneAdPublishModalProps {
  post: PostShape | null;
  isOpen: boolean;
  onClose: () => void;
  onCreated?: () => void;
}

type PublisherPlatform = "facebook" | "instagram";

const DEFAULT_FB_POSITIONS = ["feed"];
const DEFAULT_IG_POSITIONS_REEL = ["reels"];
const DEFAULT_IG_POSITIONS_FEED = ["stream"];

/**
 * Modal for publishing a post as a Meta standalone ad.
 *
 * Differs from BoostModal: no agent recommendation, no existing-post
 * dependency. The user picks budget, duration, targeting, and placements
 * and the post's primary media becomes the ad creative via
 * object_story_spec.
 *
 * Placements default from postType: Reel → Reels placement, feed post
 * → feed placement. User can adjust before submit.
 */
export const StandaloneAdPublishModal: React.FC<
  StandaloneAdPublishModalProps
> = ({ post, isOpen, onClose, onCreated }) => {
  const isReel = post?.isReel === true || post?.postType === "reel";

  const [caption, setCaption] = useState(post?.caption ?? "");
  const [dailyBudget, setDailyBudget] = useState("8.00");
  const [durationDays, setDurationDays] = useState("14");
  const [countryCode, setCountryCode] = useState("IE");
  const [ageMin, setAgeMin] = useState("18");
  const [ageMax, setAgeMax] = useState("65");
  const [includeFacebook, setIncludeFacebook] = useState(true);
  const [includeInstagram, setIncludeInstagram] = useState(true);

  const [error, setError] = useState<string | null>(null);

  // Agent-provided recommendation. Targeting interests/behaviors are
  // opaque to this modal's form — we store them here and pass straight
  // through at submit so the ad gets rich targeting even without a
  // dedicated picker UI.
  const [phase, setPhase] = useState<Phase>("recommending");
  const [recommendation, setRecommendation] =
    useState<BoostRecommendation | null>(null);
  const [recommendError, setRecommendError] = useState<string | null>(null);
  const [nonce, setNonce] = useState(0); // bumped when user clicks Rerun agent
  const [hydratedFromHistory, setHydratedFromHistory] = useState(false);
  const [priorTaskInfo, setPriorTaskInfo] = useState<{
    status: string;
    createdAt: string | null;
    errorMessage: string | null;
  } | null>(null);
  const userEditedCaptionRef = useRef(false);

  useEffect(() => {
    if (!isOpen || !post?.id) {
      setPhase("recommending");
      setRecommendation(null);
      setRecommendError(null);
      setHydratedFromHistory(false);
      setPriorTaskInfo(null);
      userEditedCaptionRef.current = false;
      return;
    }
    let cancelled = false;
    setPhase("recommending");
    setRecommendError(null);

    // Nonce > 0 means the user clicked Rerun agent — skip the cache
    // and go straight to a fresh Claude run.
    const forceAgentRun = nonce > 0;

    const loadFromHistoryThenMaybeAgent = async () => {
      if (!forceAgentRun) {
        try {
          const latest = await adsService.getLatestStandaloneAd(post.id);
          if (cancelled) return;
          const task = latest.task;
          const priorRec = task?.inputData?.agentRecommendation ?? null;
          if (task && priorRec) {
            applyRecommendation(priorRec);
            // Also hydrate form overrides the user had last time.
            const input = task.inputData;
            if (input?.dailyBudget) {
              setDailyBudget(parseFloat(input.dailyBudget).toFixed(2));
            }
            if (input?.durationDays != null) {
              setDurationDays(String(input.durationDays));
            }
            if (input?.caption != null) {
              setCaption(input.caption);
              // The caption in input_data was the user's final submission,
              // so treat it as an "edit" so regeneration doesn't stomp it.
              userEditedCaptionRef.current = true;
            }
            setRecommendation(priorRec);
            setHydratedFromHistory(true);
            setPriorTaskInfo({
              status: task.status,
              createdAt: task.createdAt,
              errorMessage: task.errorMessage,
            });
            setPhase("ready");
            return;
          }
        } catch (err) {
          // Fall through to agent run — never block the flow on a
          // cache lookup failure.
          if (cancelled) return;
        }
      }

      // Either no history or user forced a rerun — call the agent.
      try {
        const run = await adsService.recommendStandaloneAd(post.id);
        if (cancelled) return;
        const rec = run.recommendation;
        if (rec) applyRecommendation(rec);
        setRecommendation(rec);
        setHydratedFromHistory(false);
        setPriorTaskInfo(null);
        setPhase("ready");
      } catch (err) {
        if (cancelled) return;
        setRecommendError(
          err instanceof Error ? err.message : "Failed to get recommendation",
        );
        setPhase("ready");
      }
    };

    loadFromHistoryThenMaybeAgent();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, post?.id, nonce]);

  const applyRecommendation = (rec: BoostRecommendation) => {
    // The agent emits either a single 'META' block or per-platform
    // blocks. For standalone ads we asked for META-only but handle
    // both shapes: use META if present, otherwise sum FB+IG.
    const platforms = rec.platforms ?? {};
    const metaBlock = platforms.META ?? null;
    if (metaBlock) {
      if (metaBlock.dailyBudget != null) {
        setDailyBudget(metaBlock.dailyBudget.toFixed(2));
      }
      if (metaBlock.durationDays != null) {
        setDurationDays(String(metaBlock.durationDays));
      }
    } else {
      const blocks = Object.values(platforms).filter(Boolean) as Array<{
        dailyBudget?: number;
        durationDays?: number;
      }>;
      if (blocks.length > 0) {
        const totalDaily = blocks.reduce(
          (s, b) => s + (b.dailyBudget ?? 0),
          0,
        );
        if (totalDaily > 0) setDailyBudget(totalDaily.toFixed(2));
        const dur = blocks.find((b) => b.durationDays != null)?.durationDays;
        if (dur != null) setDurationDays(String(dur));
      }
    }

    const t = rec.targeting;
    if (t) {
      if (t.ageMin != null) setAgeMin(String(t.ageMin));
      if (t.ageMax != null) setAgeMax(String(t.ageMax));
      const firstLoc = t.locations?.[0];
      if (firstLoc?.countryCode) {
        setCountryCode(firstLoc.countryCode.toUpperCase());
      }
    }

    // Only overwrite the caption if the user hasn't typed anything yet.
    // Regeneration respects user edits — the agent's copy is a draft,
    // not authoritative.
    if (rec.adCopy && !userEditedCaptionRef.current) {
      setCaption(rec.adCopy);
    }
  };

  if (!post) return null;

  const recommending = phase === "recommending";
  const submitting = phase === "creating";

  const canSubmit =
    phase === "ready" &&
    parseFloat(dailyBudget) > 0 &&
    parseInt(durationDays, 10) > 0 &&
    countryCode.trim().length === 2 &&
    (includeFacebook || includeInstagram);

  const resetAndClose = () => {
    setCaption(post.caption ?? "");
    setError(null);
    userEditedCaptionRef.current = false;
    setPhase("recommending");
    onClose();
  };

  const handleSubmit = async () => {
    setPhase("creating");
    setError(null);

    const publisherPlatforms: PublisherPlatform[] = [];
    if (includeFacebook) publisherPlatforms.push("facebook");
    if (includeInstagram) publisherPlatforms.push("instagram");

    const igPositions = includeInstagram
      ? isReel
        ? DEFAULT_IG_POSITIONS_REEL
        : DEFAULT_IG_POSITIONS_FEED
      : undefined;

    // Prefer the agent's richer targeting (resolved meta_ids for
    // locations + interests + behaviors) when available. Fall back to
    // the manual country+age form for cases where the agent hasn't
    // run or returned nothing usable.
    const agentTargeting = recommendation?.targeting ?? null;
    const targeting: BoostTargeting = agentTargeting
      ? {
          ...agentTargeting,
          ageMin: parseInt(ageMin, 10),
          ageMax: parseInt(ageMax, 10),
        }
      : {
          geographicScope: "country",
          locations: [
            {
              metaId: countryCode.toUpperCase(),
              name: countryCode.toUpperCase(),
              countryCode: countryCode.toUpperCase(),
            },
          ],
          ageMin: parseInt(ageMin, 10),
          ageMax: parseInt(ageMax, 10),
          interests: [],
        };

    const request: StandaloneAdRequest = {
      postId: post.id,
      caption,
      publisherPlatforms: publisherPlatforms,
      facebookPositions: includeFacebook ? DEFAULT_FB_POSITIONS : undefined,
      instagramPositions: igPositions,
      dailyBudget: parseFloat(dailyBudget),
      durationDays: parseInt(durationDays, 10),
      objective: recommendation?.campaignType ?? "AWARENESS",
      targeting,
      agentRecommendation: recommendation,
    };

    // Fire-and-forget: the backend returns 202 immediately after
    // queuing the task. Progress and completion are delivered via
    // SSE events (standalone_ad_progress) which the realtime
    // subscriber translates into toasts + a badge on the post card.
    // We close the modal as soon as the request is accepted so the
    // user isn't blocked waiting on Meta.
    try {
      await adsService.createStandaloneAd(request);
      if (onCreated) onCreated();
      resetAndClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to queue standalone ad");
      setPhase("ready");
    }
  };

  const handleRegenerate = () => {
    setNonce((n) => n + 1);
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={resetAndClose}
      title="Publish as standalone ad"
      size="lg"
      footer={
        phase === "recommending" ? (
          <div className="flex justify-end">
            <Button variant="ghost" onClick={resetAndClose}>
              Cancel
            </Button>
          </div>
        ) : (
          <div className="flex justify-between gap-2">
            <Button variant="ghost" onClick={resetAndClose} disabled={submitting}>
              Cancel
            </Button>
            <div className="flex gap-2">
              {recommendation && !submitting && (
                <Button variant="secondary" onClick={handleRegenerate}>
                  <span className="inline-flex items-center gap-1.5">
                    <RefreshIcon className="w-4 h-4" /> Rerun agent
                  </span>
                </Button>
              )}
              <Button onClick={handleSubmit} disabled={!canSubmit}>
                {submitting ? (
                  <span className="inline-flex items-center gap-2">
                    <SpinnerIcon className="w-4 h-4" /> Queuing…
                  </span>
                ) : (
                  "Approve & Create"
                )}
              </Button>
            </div>
          </div>
        )
      }
    >
      {phase === "recommending" ? (
        <AgentRecommendingPanel />
      ) : (
      <div className="p-6 space-y-4 text-sm">
        {error && (
          <div className="rounded border border-red-200 bg-red-50 p-3 text-red-700">
            {error}
          </div>
        )}

        <div className="rounded border border-purple-200 bg-purple-50 p-3 text-xs text-purple-900">
          This ad runs as a standalone Meta campaign. Engagement lives on the
          ad, not on the original post. Metrics appear here and in Meta Ads
          Manager; they will not surface on the Instagram Reel.
        </div>

        {recommendError && (
          <div className="rounded border border-amber-300 bg-amber-50 p-3 text-xs text-amber-900">
            Agent recommendation failed ({recommendError}). You can still
            configure budget and targeting manually below.
          </div>
        )}

        {hydratedFromHistory && (
          <div className="rounded border border-gray-200 bg-gray-50 p-3 text-xs text-gray-700">
            <div className="flex items-center justify-between gap-2">
              <span>
                Loaded from a previous session (
                {priorTaskInfo?.status?.toLowerCase() ?? "prior"}
                {priorTaskInfo?.createdAt
                  ? ` · ${formatDateTime(priorTaskInfo.createdAt)}`
                  : ""}
                ). The agent reasoning below is from that run. Click
                "Rerun agent" to get a fresh opinion.
              </span>
            </div>
            {priorTaskInfo?.status?.toLowerCase() === "failed" &&
              priorTaskInfo?.errorMessage && (
                <div className="mt-2 rounded border border-red-100 bg-red-50 p-2 text-red-800">
                  Previous attempt failed: {priorTaskInfo.errorMessage}
                </div>
              )}
          </div>
        )}

        {recommendation?.reasoning && (
          <div className="rounded border border-gray-200 bg-white p-3 text-xs">
            <div className="font-medium text-gray-800">Agent reasoning</div>
            <p className="mt-1 text-gray-600">{recommendation.reasoning}</p>
          </div>
        )}

        <div className="rounded border border-blue-100 bg-blue-50 p-3 text-xs text-blue-900">
          After you approve, this modal closes and the ad is created in
          the background. You'll get a notification when it's live — no
          need to stay here.
        </div>

        <LabeledField
          label={
            recommendation?.adCopy
              ? "Ad copy (agent-drafted — edit freely)"
              : "Ad copy"
          }
        >
          <textarea
            className="w-full rounded border border-gray-300 px-2 py-1.5 text-sm"
            rows={4}
            value={caption}
            onChange={(e) => {
              userEditedCaptionRef.current = true;
              setCaption(e.target.value);
            }}
            placeholder="What should this ad say?"
          />
        </LabeledField>

        <div className="grid grid-cols-2 gap-4">
          <LabeledField label="Daily budget (€)">
            <input
              type="number"
              min="1"
              step="0.50"
              className="w-full rounded border border-gray-300 px-2 py-1.5 text-sm"
              value={dailyBudget}
              onChange={(e) => setDailyBudget(e.target.value)}
            />
          </LabeledField>
          <LabeledField label="Duration (days)">
            <input
              type="number"
              min="1"
              step="1"
              className="w-full rounded border border-gray-300 px-2 py-1.5 text-sm"
              value={durationDays}
              onChange={(e) => setDurationDays(e.target.value)}
            />
          </LabeledField>
        </div>

        <div className="rounded bg-gray-50 p-3 text-xs text-gray-600">
          Total ={" "}
          <span className="font-semibold">
            €
            {(
              parseFloat(dailyBudget || "0") *
              parseInt(durationDays || "0", 10)
            ).toFixed(2)}
          </span>{" "}
          over {durationDays || 0} day(s).
        </div>

        <LabeledField label="Country (ISO code)">
          <input
            type="text"
            maxLength={2}
            className="w-24 rounded border border-gray-300 px-2 py-1.5 text-sm uppercase"
            value={countryCode}
            onChange={(e) => setCountryCode(e.target.value.toUpperCase())}
          />
          {recommendation?.targeting?.locations &&
            recommendation.targeting.locations.length > 0 && (
              <div className="mt-1 text-xs text-gray-500">
                Agent-resolved locations:{" "}
                {recommendation.targeting.locations
                  .map((l) => l.name + (l.radiusKm ? ` (${l.radiusKm}km)` : ""))
                  .join(", ")}
              </div>
            )}
        </LabeledField>

        {recommendation?.targeting && (
          <div className="rounded border border-gray-200 bg-gray-50 p-3 text-xs">
            <div className="font-medium text-gray-800">
              Agent-resolved targeting (submitted as-is)
            </div>
            <div className="mt-1 space-y-1 text-gray-700">
              <div>
                Scope: {recommendation.targeting.geographicScope}
              </div>
              {recommendation.targeting.interests &&
                recommendation.targeting.interests.length > 0 && (
                  <div className="flex flex-wrap gap-1">
                    <span className="text-gray-500">Interests:</span>
                    {recommendation.targeting.interests.map((i) => (
                      <span
                        key={i.metaId}
                        className="rounded bg-white border border-gray-200 px-1.5 py-0.5"
                      >
                        {i.name}
                      </span>
                    ))}
                  </div>
                )}
              {recommendation.targeting.behaviors &&
                recommendation.targeting.behaviors.length > 0 && (
                  <div className="flex flex-wrap gap-1">
                    <span className="text-gray-500">Behaviors:</span>
                    {recommendation.targeting.behaviors.map((b) => (
                      <span
                        key={b.metaId}
                        className="rounded bg-white border border-gray-200 px-1.5 py-0.5"
                      >
                        {b.name}
                      </span>
                    ))}
                  </div>
                )}
            </div>
          </div>
        )}

        <div className="grid grid-cols-2 gap-4">
          <LabeledField label="Age min">
            <input
              type="number"
              min="13"
              max="65"
              className="w-full rounded border border-gray-300 px-2 py-1.5 text-sm"
              value={ageMin}
              onChange={(e) => setAgeMin(e.target.value)}
            />
          </LabeledField>
          <LabeledField label="Age max">
            <input
              type="number"
              min="13"
              max="65"
              className="w-full rounded border border-gray-300 px-2 py-1.5 text-sm"
              value={ageMax}
              onChange={(e) => setAgeMax(e.target.value)}
            />
          </LabeledField>
        </div>

        <LabeledField label="Placements">
          <div className="flex flex-col gap-1 text-sm text-gray-700">
            <label className="inline-flex items-center gap-2">
              <input
                type="checkbox"
                checked={includeFacebook}
                onChange={(e) => setIncludeFacebook(e.target.checked)}
              />
              Facebook feed
            </label>
            <label className="inline-flex items-center gap-2">
              <input
                type="checkbox"
                checked={includeInstagram}
                onChange={(e) => setIncludeInstagram(e.target.checked)}
              />
              Instagram {isReel ? "Reels" : "feed"}
            </label>
          </div>
        </LabeledField>

        <div className="rounded border border-gray-200 bg-white p-3 text-xs text-gray-600">
          <div className="font-medium text-gray-800">Creative</div>
          <div className="mt-1 text-gray-500">
            The post's prepared public media (same asset Meta would receive
            from the normal publish flow) is used as the ad creative. No
            separate upload is needed.
          </div>
        </div>
      </div>
      )}
    </Modal>
  );
};

/**
 * Loading panel shown while the agent is generating a standalone-ad
 * recommendation. Mirrors BoostModal's AgentRecommendingState rhythm:
 * big centered spinner, rotating step hints so the 20-60s agent run
 * shows progress rather than appearing hung.
 */
const AgentRecommendingPanel: React.FC = () => {
  const STEPS = [
    "Reading post and client context",
    "Checking available budget",
    "Looking up historical performance",
    "Drafting ad copy",
    "Resolving Meta targeting IDs",
    "Sizing budget and duration",
    "Finalizing recommendation",
  ];
  const [stepIndex, setStepIndex] = useState(0);
  const [elapsedSeconds, setElapsedSeconds] = useState(0);

  useEffect(() => {
    const stepTimer = setInterval(() => {
      setStepIndex((i) => Math.min(i + 1, STEPS.length - 1));
    }, 5_500);
    const tickTimer = setInterval(() => {
      setElapsedSeconds((s) => s + 1);
    }, 1_000);
    return () => {
      clearInterval(stepTimer);
      clearInterval(tickTimer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="py-10 flex flex-col items-center text-center space-y-5">
      <div className="relative">
        <div className="w-16 h-16 rounded-full border-4 border-blue-100 border-t-blue-500 animate-spin" />
      </div>
      <div>
        <div className="text-base font-medium text-gray-900">
          Generating standalone-ad recommendation
        </div>
        <div className="mt-1 text-xs text-gray-500">
          Elapsed {elapsedSeconds}s
        </div>
      </div>
      <div className="w-full max-w-md">
        <ul className="space-y-1 text-sm text-gray-700">
          {STEPS.map((s, i) => (
            <li
              key={s}
              className={
                i < stepIndex
                  ? "text-gray-400 line-through"
                  : i === stepIndex
                    ? "text-blue-600 font-medium"
                    : "text-gray-400"
              }
            >
              {i < stepIndex ? "✓ " : i === stepIndex ? "→ " : "  "}
              {s}
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
};

const LabeledField: React.FC<{
  label: string;
  children: React.ReactNode;
}> = ({ label, children }) => (
  <label className="block space-y-1">
    <span className="block text-xs font-medium text-gray-500 uppercase">
      {label}
    </span>
    {children}
  </label>
);

